/**
 * Reusable Legal/Policy document template — renders any of the 6 SOC 2
 * evidence documents (Code of Conduct, Risk Assessment, Backup Runbook,
 * Incident Response, Capacity Planning, SLA) with consistent styling.
 *
 * Auditor-friendly: each doc has a `last_reviewed` date, version tag, and
 * "Linked SOC 2 controls" footer so A-LIGN can trace evidence → control.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { ShieldCheck, FileCheck, Clock, ArrowLeft } from 'lucide-react';

export default function LegalDoc({
  slug,
  title,
  subtitle,
  version = '1.0',
  lastReviewed,
  ownedBy = 'Founder',
  controls = [],
  children,
}) {
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid={`legal-doc-${slug}`}>
      <Helmet>
        <title>{title} | CARTIEAI</title>
        <meta name="description" content={subtitle} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/soc2-readiness" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-emerald-300 mb-8 transition-colors">
          <ArrowLeft className="w-3 h-3" /> Back to SOC 2 readiness
        </Link>

        {/* Header */}
        <div className="mb-10 border-b border-slate-800 pb-8">
          <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[10px] font-mono uppercase tracking-widest mb-4">
            <ShieldCheck className="w-3 h-3 mr-1.5 inline" />
            SOC 2 evidence document
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">{title}</h1>
          <p className="text-base text-slate-400">{subtitle}</p>

          <div className="flex flex-wrap gap-4 mt-5 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><FileCheck className="w-3 h-3" /> Version {version}</span>
            {lastReviewed && <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> Last reviewed {lastReviewed}</span>}
            <span>Owner: {ownedBy}</span>
          </div>
        </div>

        {/* Body */}
        <article className="prose-doc">
          {children}
        </article>

        {/* Linked controls footer */}
        {controls.length > 0 && (
          <div className="mt-12 pt-6 border-t border-slate-800">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Linked SOC 2 controls</div>
            <div className="flex flex-wrap gap-2">
              {controls.map(c => (
                <Badge key={c} className="bg-slate-800 border border-slate-700 text-slate-300 text-[11px] font-mono">{c}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightweight typography overrides (we don't pull @tailwindcss/typography) */}
      <style>{`
        .prose-doc { color: rgba(226,232,240,.92); line-height: 1.7; font-size: 15px; }
        .prose-doc h2 { font-size: 1.4rem; font-weight: 700; color: #fff; margin: 2rem 0 .8rem; border-bottom: 1px solid rgba(71,85,105,.4); padding-bottom: .3rem; }
        .prose-doc h3 { font-size: 1.1rem; font-weight: 600; color: #f1f5f9; margin: 1.5rem 0 .5rem; }
        .prose-doc p { margin: .8rem 0; }
        .prose-doc ul, .prose-doc ol { margin: .8rem 0; padding-left: 1.4rem; }
        .prose-doc li { margin: .35rem 0; }
        .prose-doc code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 13px; background: rgba(30,41,59,.6); color: #67e8f9; padding: 1px 6px; border-radius: 4px; }
        .prose-doc strong { color: #fff; font-weight: 600; }
        .prose-doc a { color: #6ee7b7; text-decoration: underline; text-underline-offset: 3px; }
        .prose-doc a:hover { color: #a7f3d0; }
        .prose-doc blockquote { border-left: 3px solid rgba(16,185,129,.4); padding: .4rem 0 .4rem 1rem; margin: 1rem 0; color: rgba(203,213,225,.85); font-style: italic; background: rgba(30,41,59,.3); border-radius: 0 6px 6px 0; }
        .prose-doc table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 13px; }
        .prose-doc th, .prose-doc td { border: 1px solid rgba(71,85,105,.4); padding: .5rem .75rem; text-align: left; }
        .prose-doc th { background: rgba(30,41,59,.6); color: #fff; font-weight: 600; }
      `}</style>
    </div>
  );
}
